// Вкладка «Отбор» в кабинете сопровождения: доска созвонов-обучений с преподами.
// Открывается на весь экран (боковые чаты прячет SupportDashboard). Данные —
// GET /api/otbor/board (очередь платформы без Лидеров + статистика пробных за
// 30 дней + слоты + журнал отсмотров). Макет: preview/_otbor-preview.html,
// механика (Антон 06.08): менеджер выставляет слоты по 3 места, преподы позже
// будут записываться сами через ЛК; пока менеджер сажает в слот вручную.
import { useState, useEffect, useCallback, useRef } from 'react'
import api from '../api'
import TeacherRating from './TeacherRating'
import OtborActive from './OtborActive'
import { useLessonMoments, MomentsTimeline, MomentsMarkButtons, MomentsTab } from './LessonMoments'

// Черновые пункты разбора — Антон обещал дать свои, эти только чтобы показать устройство
const DRAFT_QUESTIONS = [
  'Начал урок вовремя, поздоровался, представился',
  'Спросил, что уже проходили, и сказал, что будет на уроке',
  'Держит контакт: задаёт вопросы, не читает лекцию',
  'Объясняет понятно, без лишних терминов',
  'Успел разобрать тему до конца, не оборвал на полуслове',
  'В конце сказал, что дальше, и позвал заниматься постоянно',
]
const MARKS = [['bad', 'плохо'], ['mid', 'средне'], ['ok', 'хорошо']]
const MARK_STYLE = {
  bad: { background: '#FEF2F2', borderColor: '#FECACA', color: '#B91C1C' },
  mid: { background: '#FFFBEB', borderColor: '#FDE68A', color: '#B45309' },
  ok: { background: '#ECFDF5', borderColor: '#A7F3D0', color: '#047857' },
}

// Окно отсмотра записи пробного урока: слева плеер, справа разбор + итог.
// Запись ищется по клиенту (комната) и времени урока; половина пробных не
// записана (запись держится на браузере препода) — тогда честная плашка.
function ReviewModal({ teacher, onClose, onSaved }) {
  const [rec, setRec] = useState({ state: 'loading' })
  const [marks, setMarks] = useState({})
  const [recText, setRecText] = useState('')
  const [noteText, setNoteText] = useState('')
  const [saving, setSaving] = useState(false)
  const videoRef = useRef(null)
  const [fast, setFast] = useState(false)
  const t = teacher
  // отметка «хороших моментов» (LessonMoments.js): вкладка справа, полоска и кнопки у плеера
  const [tab, setTab] = useState('review')
  const mm = useLessonMoments({ recPath: rec.state === 'found' ? rec.path : null, teacher: t, videoRef, active: rec.state === 'found', durationSec: rec.durationSec })
  const showMoments = rec.state === 'found' && tab === 'moments'

  useEffect(() => {
    let alive = true
    const lt = t.lastTrial
    if (!lt || !lt.mkUserId) { setRec({ state: 'none', reason: 'У этого урока нет клиента платформы' }); return }
    // доска уже нашла файл — открываем сразу, без повторного поиска
    if (lt.recording) { setRec({ state: 'found', ...lt.recording }); return }
    api.get('/api/otbor/recording', { params: { mkUserId: lt.mkUserId, date: lt.date, time: lt.time } })
      .then(r => { if (alive) setRec(r.data.found ? { state: 'found', ...r.data } : { state: 'none', reason: r.data.reason }) })
      .catch(e => { if (alive) setRec({ state: 'none', reason: e.response?.data?.error || e.message }) })
    return () => { alive = false }
  }, [t])

  const seek = (sec) => { const v = videoRef.current; if (v) v.currentTime = Math.max(0, v.currentTime + sec) }
  const toggleSpeed = () => { const v = videoRef.current; if (v) { v.playbackRate = fast ? 1 : 1.5; setFast(!fast) } }

  const saveOutcome = async (outcome) => {
    setSaving(true)
    try {
      await api.post('/api/otbor/reviews', {
        teacher_mk_id: t.mkId, teacher_name: t.name, kind: 'recording', outcome,
        marks, recommendation: recText, colleague_note: noteText,
      })
      onSaved()
    } catch (e) {
      alert(e.response?.data?.error || e.message)
      setSaving(false)
    }
  }

  const streamSrc = rec.state === 'found'
    ? (api.defaults.baseURL || '') + '/api/otbor/recording/stream?path=' + encodeURIComponent(rec.path)   /* токен в cookie */
    : null

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: '#F1F5F9' }}>
      <div className="bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-3.5 shrink-0">
        <button className="text-sky-700 text-[13.5px] font-bold" onClick={onClose}>‹ К доске</button>
        <div>
          <b className="text-[15px]">{t.name}<TeacherRating mkId={t.mkId} rating={t.rating} reviewCount={t.reviewCount} /></b>
          <div className="text-[12px] text-slate-500">
            {(t.subjects || []).join(', ')} · первый урок · {t.lastTrial ? fmtTrialDate(t.lastTrial) : ''}
            {t.lastTrial && t.lastTrial.student ? <> · ученик: <b className="text-slate-700">{t.lastTrial.student}</b></> : ''}
            {rec.state === 'found' && rec.startedAtMsk ? ' · запись с ' + rec.startedAtMsk : ''}
            {rec.state === 'found' && rec.durationSec != null ? ' · ' + fmtDur(rec.durationSec) : ''}
            {(() => { const o = trialOutcome(t.lastTrial); return o ? <> · <span style={{ color: o.color, fontWeight: 600 }}>{o.text}</span></> : null })()}
          </div>
        </div>
      </div>
      <div className="flex-1 flex gap-3.5 p-3.5 min-h-0">
        <div className="flex-1 min-w-0 rounded-2xl overflow-hidden flex flex-col" style={{ background: '#0F172A' }}>
          {rec.state === 'loading' && (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-[13px]">Ищем запись урока на диске…</div>
          )}
          {rec.state === 'none' && (
            <div className="flex-1 flex flex-col items-center justify-center gap-2 px-8 text-center">
              <div className="text-slate-200 text-[14px] font-bold">Записи нет</div>
              <div className="text-slate-400 text-[12.5px] leading-relaxed">{rec.reason}</div>
            </div>
          )}
          {rec.state === 'found' && (
            <>
              <video ref={videoRef} src={streamSrc} controls className="flex-1 min-h-0 w-full" style={{ background: '#000' }} />
              <MomentsTimeline mm={mm} />
              <div className="shrink-0 flex items-center gap-2 px-3 py-2.5" style={{ borderTop: '1px solid #1E293B' }}>
                <button onClick={() => seek(-15)} className="rounded-lg px-3 py-1.5 text-[12.5px]" style={{ background: '#1E293B', color: '#CBD5E1' }}>−15 сек</button>
                <button onClick={() => seek(15)} className="rounded-lg px-3 py-1.5 text-[12.5px]" style={{ background: '#1E293B', color: '#CBD5E1' }}>+15 сек</button>
                <button onClick={toggleSpeed} className="rounded-lg px-3 py-1.5 text-[12.5px]" style={{ background: fast ? '#0369A1' : '#1E293B', color: fast ? '#fff' : '#CBD5E1' }}>
                  Скорость 1,5×
                </button>
                <MomentsMarkButtons mm={mm} onOpenTab={() => setTab('moments')} />
              </div>
            </>
          )}
        </div>
        <div className="w-[400px] shrink-0 bg-white border border-slate-200 rounded-2xl flex flex-col overflow-hidden">
          {rec.state === 'found' && (
            <div className="flex shrink-0" style={{ borderBottom: '1px solid #F1F5F9' }}>
              {[['review', 'Разбор урока'], ['moments', 'Моменты для карточки']].map(([k, label]) => (
                <button key={k} onClick={() => setTab(k)} className="flex-1 py-3 text-[13px] font-bold"
                  style={{ background: 'none', border: 0, borderBottom: '2px solid ' + (tab === k ? '#0369A1' : 'transparent'), color: tab === k ? '#0369A1' : '#64748B', cursor: 'pointer' }}>
                  {label}
                  {k === 'moments' && mm.mine.length > 0 && (
                    <span className="ml-1.5 rounded-full px-1.5 text-[11px]" style={{ background: '#E0F2FE', color: '#0369A1' }}>{mm.mine.length}</span>
                  )}
                </button>
              ))}
            </div>
          )}
          {!showMoments && (<>
          <div className="p-3.5 border-b border-slate-100 shrink-0">
            <b className="text-[14px]">Разбор урока</b>
            <div className="text-[11.5px] text-slate-400 mt-1 leading-snug">
              Отмечаем по ходу просмотра. Заполненный разбор идёт в план сопроводителя — 250 ₽ за отсмотр.
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-3.5 py-2.5">
            <div className="rounded-xl px-2.5 py-2 mb-2.5 text-[11.5px] leading-snug" style={{ background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E' }}>
              Пункты черновые — ты говорил, что дашь свои. Пока они здесь, чтобы было видно устройство.
            </div>
            {DRAFT_QUESTIONS.map((qt, i) => (
              <div key={i} className="py-2 border-b border-slate-50">
                <div className="text-[12.5px] leading-snug mb-1.5">{qt}</div>
                <div className="flex gap-1.5">
                  {MARKS.map(([k, label]) => (
                    <button key={k} onClick={() => setMarks({ ...marks, [i]: marks[i] === k ? undefined : k })}
                      className="flex-1 rounded-lg border text-[11px] font-bold py-1"
                      style={marks[i] === k ? MARK_STYLE[k] : { borderColor: '#E2E8F0', color: '#64748B', background: '#fff' }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="mt-3">
              <div className="text-[12px] font-bold mb-1">Рекомендации преподавателю</div>
              <div className="text-[11px] text-slate-400 mb-1">Это увидит сам преподаватель</div>
              <textarea value={recText} onChange={e => setRecText(e.target.value)}
                placeholder="Например: не спросила, что уже проходили в школе — 10 минут объясняла известное"
                className="w-full h-16 border border-slate-200 rounded-xl px-2.5 py-2 text-[12.5px] outline-none focus:border-sky-300 resize-none" />
            </div>
            <div className="mt-2.5">
              <div className="text-[12px] font-bold mb-1">Пометка для коллег при подборе</div>
              <div className="text-[11px] text-slate-400 mb-1">Это увидят звонильщик и сопроводитель, когда будут ставить клиента на урок</div>
              <textarea value={noteText} onChange={e => setNoteText(e.target.value)}
                placeholder="Например: сильна с малышами, на ЕГЭ не ставить"
                className="w-full h-16 border border-slate-200 rounded-xl px-2.5 py-2 text-[12.5px] outline-none focus:border-sky-300 resize-none" />
            </div>
          </div>
          </>)}
          {showMoments && <MomentsTab mm={mm} />}
          <div className="shrink-0 border-t border-slate-100 p-3">
            <div className="grid grid-cols-2 gap-1.5">
              <button disabled={saving} onClick={() => saveOutcome('ok')}
                className="rounded-lg text-white text-[11.5px] font-bold py-2 disabled:opacity-50" style={{ background: '#047857' }}>
                Всё хорошо, созвон не нужен
              </button>
              <button disabled={saving} onClick={() => saveOutcome('needcall')}
                className="rounded-lg text-white text-[11.5px] font-bold py-2 disabled:opacity-50" style={{ background: '#0369A1' }}>
                Есть правки, нужен созвон
              </button>
              <button disabled={saving} onClick={() => saveOutcome('watch')}
                className="rounded-lg text-white text-[11.5px] font-bold py-2 disabled:opacity-50" style={{ background: '#B45309' }}>
                Требует внимания
              </button>
              <button disabled={saving} onClick={() => saveOutcome('removed')}
                className="rounded-lg text-white text-[11.5px] font-bold py-2 disabled:opacity-50" style={{ background: '#B91C1C' }}>
                Не подошёл
              </button>
            </div>
            <div className="text-[10.5px] text-slate-400 mt-2 leading-snug">
              Итог перекладывает преподавателя в соответствующую колонку доски. «Есть правки» — преподаватель
              попадёт в колонку «Нужен созвон». Снятие с платформы и задачи «Переставить ученика» подключим
              отдельным шагом — пока это только запись в журнале.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']

function isoToday(offset = 0) {
  const d = new Date(Date.now() + offset * 864e5)
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}
function fmtDay(iso) {
  if (!iso) return ''
  if (iso === isoToday()) return 'Сегодня'
  if (iso === isoToday(1)) return 'Завтра'
  const [, m, d] = iso.split('-')
  return Number(d) + ' ' + (MONTHS[Number(m) - 1] || m)
}
function fmtTrialDate(t) {
  if (!t) return null
  return fmtDay(t.date) + (t.time ? ', ' + t.time : '')
}
// Итог пробного из отметок, которые УЖЕ есть в системе (ничего не выдумываем):
// статус урока от препода + поставлено ли расписание + выбор клиента + удаление с причиной
const REMOVAL_LABELS = {
  trial_refused: 'Отказ после первого урока',
  no_contact: 'Не вышли на связь',
  tutor_decision: 'Решение репетитора',
  other: 'Удалён из расписания',
}
function trialOutcome(lt) {
  if (!lt) return null
  const choice = lt.choice === 'other' ? ' · клиент выбрал другого препода' : ''
  if (lt.removed) {
    const label = REMOVAL_LABELS[lt.removed.code] || 'Удалён из расписания'
    return { color: '#B91C1C', text: label + (lt.removed.text ? ' — ' + lt.removed.text : '') }
  }
  if (lt.status === 'noshow') return { color: '#B45309', text: 'Клиент не пришёл на урок' }
  if (lt.status === 'done') {
    if (lt.stayed) return { color: '#047857', text: 'Проведён · расписание поставлено' + (lt.next ? ' (след. урок ' + fmtDay(lt.next.date) + ')' : '') + choice }
    return { color: '#B45309', text: 'Проведён · расписания нет' + choice }
  }
  return { color: '#94A3B8', text: 'Препод ещё не отметил урок' }
}

// Длительность записи по-человечески: «20 сек», «6 мин», «1 ч 06 мин»
function fmtDur(sec) {
  if (sec == null) return null
  if (sec < 60) return sec + ' сек'
  const h = Math.floor(sec / 3600), m = Math.round((sec % 3600) / 60)
  return h ? h + ' ч ' + String(m).padStart(2, '0') + ' мин' : m + ' мин'
}

// Шапка слота: строка 1 — дата·время + заполненность, строка 2 — «Ведёт» +
// «Удалить слот». ОДНА на доску и окно «Слоты созвонов» (Антон 07.08: без дублей).
// wide=true — крупный вариант для окна (полная дата, «свободно N места»).
function SlotHead({ s, busy, onDelete, wide }) {
  const n = s.members.length
  const pill = n >= s.capacity ? 'bg-slate-100 text-emerald-700 font-extrabold'
    : n > 0 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'
  const pad = wide ? '' : ' px-0.5'
  return (
    <>
      <div className={'flex items-center gap-2' + pad}>
        <b className={(wide ? 'text-[13.5px]' : 'text-[12px]') + ' whitespace-nowrap'}>
          {fmtDay(s.slot_date)}{wide ? ', ' + s.slot_date.split('-').reverse().slice(0, 2).join('.') : ''} · {s.slot_time}
        </b>
        <span className={'ml-auto text-[11px] font-bold rounded-full px-2 py-0.5 whitespace-nowrap ' + pill}>
          {n > 0 ? n + ' из ' + s.capacity : wide ? 'свободно ' + s.capacity + ' места' : 'свободно'}
        </span>
      </div>
      <div className={'flex items-center mt-0.5' + pad + (wide ? '' : ' mb-1.5')}>
        {s.created_by && (
          <span className={(wide ? 'text-[11px]' : 'text-[10.5px]') + ' text-slate-400'}>Ведёт: {s.created_by}</span>
        )}
        <button disabled={busy} onClick={onDelete}
          className={'ml-auto ' + (wide ? 'text-[11px]' : 'text-[10.5px]') + ' text-red-700 hover:underline whitespace-nowrap'}>
          Удалить слот
        </button>
      </div>
    </>
  )
}

// Итог созвона-обучения (Антон 06.08): БЕЗ оценок — менеджер записывает инфу
// о репетиторе и переносит его в колонку «Просмотр первого урока». Оценки и рекомендации
// ставятся позже, при отсмотре записи реального пробного.
function CallOutcomeModal({ teacher, slotId, onClose, onSaved }) {
  const [noteText, setNoteText] = useState('')
  const [saving, setSaving] = useState(false)
  const t = teacher
  const save = async (outcome) => {
    setSaving(true)
    try {
      await api.post('/api/otbor/reviews', {
        teacher_mk_id: t.mkId, teacher_name: t.name, kind: 'call', slot_id: slotId,
        outcome, colleague_note: noteText,
      })
      onSaved()
    } catch (e) {
      alert(e.response?.data?.error || e.message)
      setSaving(false)
    }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-24" style={{ background: 'rgba(15,23,42,.45)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-white rounded-2xl w-[460px] max-w-full overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <b className="text-[14px]">Итог созвона — {t.name}</b>
          <div className="text-[11.5px] text-slate-400 mt-1 leading-snug">
            Запишите, что обсудили с репетитором, — это увидят коллеги при подборе.
          </div>
        </div>
        <div className="p-4">
          <textarea value={noteText} onChange={e => setNoteText(e.target.value)} autoFocus
            placeholder="Например: спокойная, опыт с 5–6 классами, ОГЭ не берёт; камера и звук в порядке"
            className="w-full h-24 border border-slate-200 rounded-xl px-2.5 py-2 text-[12.5px] outline-none focus:border-sky-300 resize-none" />
          <div className="mt-3 grid grid-cols-3 gap-2">
            <button disabled={saving || !noteText.trim()} onClick={() => save('ok')}
              className="rounded-xl text-white text-[12.5px] font-bold py-2.5 disabled:opacity-50" style={{ background: '#047857' }}>
              Работает с нами
            </button>
            <button disabled={saving || !noteText.trim()} onClick={() => save('watch')}
              className="rounded-xl text-white text-[12.5px] font-bold py-2.5 disabled:opacity-50" style={{ background: '#B45309' }}>
              Требует внимания
            </button>
            <button disabled={saving || !noteText.trim()} onClick={() => save('removed')}
              className="rounded-xl text-white text-[12.5px] font-bold py-2.5 disabled:opacity-50" style={{ background: '#B91C1C' }}>
              Не подошёл
            </button>
          </div>
          <div className="text-[10.5px] text-slate-400 mt-2 leading-snug">
            «Работает с нами» — в колонку «Работают с нами», «Требует внимания» — в «Требуют внимания», «Не подошёл» — в «Не подошли».
          </div>
        </div>
      </div>
    </div>
  )
}

// «Работа менеджеров»: отсмотры пробных + созвоны по каждому сопроводителю,
// 250 ₽ за штуку (групповой созвон = один созвон). Данные — из журнала итогов.
function ManagerStatsModal({ onClose }) {
  const [period, setPeriod] = useState('today')
  const [data, setData] = useState(null)
  useEffect(() => {
    setData(null)
    api.get('/api/otbor/manager-stats', { params: { period } })
      .then(r => setData(r.data))
      .catch(e => setData({ error: e.response?.data?.error || e.message }))
  }, [period])
  const PERIODS = [['today', 'Сегодня'], ['week', 'Неделя'], ['month', 'Месяц']]
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-20" style={{ background: 'rgba(15,23,42,.45)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-white rounded-2xl w-[520px] max-w-full overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-start gap-2.5">
          <div>
            <b className="text-[15px]">Работа менеджеров</b>
            <div className="text-[11.5px] text-slate-400 mt-0.5 leading-snug">
              Отсмотры первых уроков и созвоны-обучения, по 250 ₽ за штуку. Групповой созвон считается одним.
            </div>
          </div>
          <button className="ml-auto text-slate-400 text-lg px-1.5" onClick={onClose}>✕</button>
        </div>
        <div className="px-4 pt-3 flex gap-1.5">
          {PERIODS.map(([k, label]) => (
            <button key={k} onClick={() => setPeriod(k)}
              className={'rounded-lg px-3 py-1.5 text-[12.5px] font-bold ' +
                (period === k ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-500 hover:text-slate-800')}>
              {label}
            </button>
          ))}
        </div>
        <div className="p-4">
          {!data && <div className="text-[12.5px] text-slate-400 text-center py-6">Считаем…</div>}
          {data && data.error && <div className="text-[12.5px] text-red-700 text-center py-6">{data.error}</div>}
          {data && data.managers && data.managers.length === 0 && (
            <div className="text-[12.5px] text-slate-400 text-center py-6">За этот период отсмотров и созвонов не было</div>
          )}
          {data && data.managers && data.managers.length > 0 && (
            <table className="w-full text-[13px]" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr className="text-[10.5px] uppercase tracking-wide text-slate-400 text-left">
                  <th className="pb-2 font-bold">Менеджер</th>
                  <th className="pb-2 font-bold text-center">Отсмотры</th>
                  <th className="pb-2 font-bold text-center">Созвоны</th>
                  <th className="pb-2 font-bold text-right">К оплате</th>
                </tr>
              </thead>
              <tbody>
                {data.managers.map(m => (
                  <tr key={m.name} className="border-t border-slate-100">
                    <td className="py-2 font-semibold">{m.name}</td>
                    <td className="py-2 text-center">{m.reviews}</td>
                    <td className="py-2 text-center">{m.calls}</td>
                    <td className="py-2 text-right font-extrabold" style={{ color: '#047857' }}>
                      {m.pay.toLocaleString('ru-RU')} ₽
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

// Процент конверсии: зелёный >70, красный <30, жёлтый между (правило Антона 06.08)
function ConvBadge({ value }) {
  const style = value == null
    ? { color: '#94A3B8', background: '#F8FAFC', border: '1px solid #E2E8F0' }
    : value > 70 ? { color: '#047857', background: '#ECFDF5', border: '1px solid #A7F3D0' }
    : value < 30 ? { color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA' }
    : { color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A' }
  return (
    <span className="shrink-0 text-[12px] font-extrabold rounded-lg px-1.5 py-0.5" style={style}>
      {value == null ? '—' : value + '%'}
    </span>
  )
}

// Карточка препода: имя + предмет, конверсия в углу, дробь «поставлено/провёл/остались»,
// постоянные клиенты, свежий пробный. actions — кнопки снизу (задаёт колонка).
function TeacherCard({ t, actions, onReview }) {
  const hasTrials = t.trialsBooked > 0
  return (
    <div className="border border-slate-200 rounded-xl p-2.5 mb-2 bg-white">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-bold leading-tight">{t.name}<TeacherRating mkId={t.mkId} rating={t.rating} reviewCount={t.reviewCount} /></div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">{(t.subjects || []).join(', ') || '—'}</div>
        </div>
        <ConvBadge value={t.conversion} />
      </div>
      {t.colleagueNote && (
        <div className="mt-1.5 text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-2 py-1.5 leading-snug">
          {t.colleagueNote}
        </div>
      )}
      {hasTrials ? (
        <>
          <div className="mt-2">
            <span className="text-[16px] font-extrabold tracking-wide">
              {t.trialsBooked}<span className="text-slate-300 font-normal px-px">/</span>
              <span style={{ color: '#0369A1' }}>{t.trialsDone}</span><span className="text-slate-300 font-normal px-px">/</span>
              <span style={{ color: '#047857' }}>{t.trialsStayed}</span>
            </span>
            <div className="text-[10px] text-slate-400 mt-0.5">поставлено · провёл · остались</div>
          </div>
          <div className="text-[11px] text-slate-400 mt-1">Занимаются сейчас — {t.regularStudents}</div>
          {t.lastTrial && (
            <div className="mt-2 pt-2 border-t border-dashed border-slate-200">
              <div className="text-[11px] text-slate-400">Свежий первый урок — {fmtTrialDate(t.lastTrial)}</div>
              {(() => { const o = trialOutcome(t.lastTrial); return o ? (
                <div className="text-[11px] font-semibold mt-0.5 leading-snug" style={{ color: o.color }}>{o.text}</div>
              ) : null })()}
              {t.lastTrial.reviewedAt ? (
                <div className="mt-1.5 rounded-lg border border-emerald-100 bg-emerald-50 text-emerald-700 text-[11px] text-center font-bold py-1.5">
                  Просмотрено{t.lastTrial.reviewedBy ? ' (' + t.lastTrial.reviewedBy + ')' : ' — оценка дана'}
                </div>
              ) : t.lastTrial.recording && onReview ? (
                <button onClick={() => onReview(t)}
                  className="mt-1.5 w-full rounded-lg border border-sky-200 bg-sky-50 text-sky-800 text-[11.5px] font-bold py-1.5 hover:bg-sky-100">
                  Посмотреть первый урок
                  {t.lastTrial.recording.durationSec != null ? ' · ' + fmtDur(t.lastTrial.recording.durationSec) : ''}
                </button>
              ) : t.lastTrial.shortRecording ? (
                <div className="mt-1.5 rounded-lg border border-dashed border-slate-200 bg-slate-50 text-slate-400 text-[11px] text-center py-1.5">
                  Запись {fmtDur(t.lastTrial.shortRecording.durationSec)} — урок не состоялся
                </div>
              ) : (
                <div className="mt-1.5 rounded-lg border border-dashed border-slate-200 bg-slate-50 text-slate-400 text-[11px] text-center py-1.5">
                  Записи нет — урок не записался
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="text-[11px] text-slate-400 mt-1.5">
          Первого урока ещё не было
          {t.regularStudents > 0 && <> · постоянных {t.regularStudents}</>}
        </div>
      )}
      {actions}
    </div>
  )
}

function ColHead({ color, title, n }) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
      <b className="text-[12.5px]">{title}</b>
      <span className="text-[11.5px] text-slate-400">{n}</span>
    </div>
  )
}

const SHOW_STEP = 12

// Созвон идёт в нашей видеоплатформе — у каждого слота своя комната
const slotRoomHref = (s) => 'https://video.ctg.school/room/' + encodeURIComponent(s.video_room || '') +
  '?role=teacher&name=' + encodeURIComponent(localStorage.getItem('name') || 'Менеджер')

function OtborBoard({ scope, onCounts }) {
  const [board, setBoard] = useState(null)
  const [err, setErr] = useState(null)
  const [q, setQ] = useState('')
  const [showAll, setShowAll] = useState(false)
  const [slotsOpen, setSlotsOpen] = useState(false)
  const [newDate, setNewDate] = useState(isoToday(1))
  const [newTime, setNewTime] = useState('15:00')
  const [busy, setBusy] = useState(false)
  const [pickFor, setPickFor] = useState(null) // препод, которому выбираем слот
  const [review, setReview] = useState(null)   // препод, чей пробный смотрим
  const [callOutcome, setCallOutcome] = useState(null) // {teacher, slotId} — итог созвона
  const [statsOpen, setStatsOpen] = useState(false)    // окно «Работа менеджеров»

  const load = useCallback(() => {
    api.get('/api/otbor/board', scope ? { params: { scope } } : undefined)
      .then(r => { setBoard(r.data); setErr(null); const c = r.data.counts || {}; if (onCounts) onCounts((c.waiting || 0) + (c.needcall || 0) + (c.scheduled || 0) + (c.working || 0) + (c.watch || 0)) })
      .catch(e => setErr(e.response?.data?.error || e.message))
  }, [scope, onCounts])
  useEffect(() => { load() }, [load])

  const act = async (fn, okMsg) => {
    setBusy(true)
    try { await fn(); load() }
    catch (e) { alert(e.response?.data?.error || e.message) }
    finally { setBusy(false) }
  }

  const addSlot = () => act(() => api.post('/api/otbor/slots', { date: newDate, time: newTime }))
  const delSlot = (s) => {
    if (s.members.length > 0 && !window.confirm(
      `Удалить слот ${fmtDay(s.slot_date)} ${s.slot_time}? Записанные преподаватели (${s.members.length}) будут сняты с созвона, каждому уйдёт уведомление об отмене.`)) return
    act(() => api.delete('/api/otbor/slots/' + s.id))
  }
  const removeMember = (slotId, memberId) => act(() => api.delete('/api/otbor/slots/' + slotId + '/members/' + memberId))
  const completeSlot = (s) => {
    if (!window.confirm(`Завершить созвон ${fmtDay(s.slot_date)} ${s.slot_time}? Окошко уйдёт из колонки.`)) return
    act(() => api.post('/api/otbor/slots/' + s.id + '/complete'))
  }
  const assign = (slot, t) => {
    setPickFor(null)
    act(() => api.post('/api/otbor/slots/' + slot.id + '/members', { teacher_mk_id: t.mkId, teacher_name: t.name }))
  }

  if (err) return (
    <div className="p-8 text-center text-[13px] text-slate-500">
      Доска не загрузилась: {err}
      <div><button className="mt-3 text-sky-700 font-semibold" onClick={load}>Попробовать ещё раз</button></div>
    </div>
  )
  if (!board) return <div className="p-8 text-center text-[13px] text-slate-400">Загрузка доски…</div>

  const ql = q.trim().toLowerCase()
  const waiting = ql ? board.waiting.filter(t => t.name.toLowerCase().includes(ql)) : board.waiting
  const withTrials = waiting.filter(t => t.trialsBooked > 0)
  const noTrials = waiting.filter(t => t.trialsBooked === 0)
  const noTrialsShown = (showAll || ql) ? noTrials : noTrials.slice(0, SHOW_STEP)

  const assignBtn = (t) => (
    <button disabled={busy} onClick={() => setPickFor(t)}
      className="mt-1.5 w-full rounded-lg border border-sky-200 bg-sky-50 text-sky-800 text-[11.5px] font-bold py-1.5 hover:bg-sky-100 disabled:opacity-50">
      Назначить созвон
    </button>
  )

  const emptyCol = (text) => <div className="text-[11.5px] text-slate-300 leading-relaxed">{text}</div>

  return (
    <div className="px-4 pt-4 pb-10">
      <div className="flex items-center gap-3 mb-3">
        <div>
          <h1 className="text-[17px] font-bold">Отбор преподавателей</h1>
          <div className="text-[12.5px] text-slate-400">Смотрим первые уроки новых клиентов, проводим созвоны-обучения, решаем — работаем дальше или нет</div>
        </div>
        <button onClick={() => setStatsOpen(true)}
          className="ml-auto rounded-xl border border-slate-200 bg-white text-slate-600 text-[13px] font-bold px-3.5 py-2 hover:border-sky-300 hover:text-sky-800">
          Работа менеджеров
        </button>
        <button onClick={() => setSlotsOpen(true)}
          className="rounded-xl bg-sky-600 text-white text-[13px] font-bold px-3.5 py-2 hover:bg-sky-700">
          Слоты созвонов{board.counts.slots > 0 && <span className="font-normal"> · {board.counts.slots}</span>}
        </button>
      </div>

      <div className="flex gap-3 items-start overflow-x-auto">
        {/* ── Первый урок: смотрим (03.10: вместо «Ждут созвон»; сюда же прежние «trained») ── */}
        <div className="flex-1 min-w-[230px] bg-white border border-slate-200 rounded-2xl p-2.5">
          <ColHead color="#38BDF8" title="Первый урок: смотрим" n={board.counts.waiting} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Поиск препода по фамилии"
            className="w-full border border-slate-200 bg-slate-50 rounded-lg px-2.5 py-1.5 text-[12px] mb-2 outline-none focus:border-sky-300 focus:bg-white" />
          {withTrials.length > 0 && (
            <div className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wide mb-1.5 mt-1 px-0.5">
              Первый урок уже был — {withTrials.length}
            </div>
          )}
          {withTrials.map(t => <TeacherCard key={t.mkId} t={t} onReview={setReview}
            actions={(t.lastTrial && !t.lastTrial.recording && !t.lastTrial.reviewedAt) ? (
              <>
                <div className="text-[11px] text-slate-400 mt-1.5 leading-snug">Можно подождать следующий урок или назначить созвон.</div>
                {assignBtn(t)}
              </>
            ) : null} />)}
          {noTrials.length > 0 && (
            <div className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wide mb-1.5 mt-2.5 px-0.5">
              Первого урока ещё не было — {noTrials.length}
            </div>
          )}
          {noTrialsShown.map(t => <TeacherCard key={t.mkId} t={t} />)}
          {!showAll && !ql && noTrials.length > noTrialsShown.length && (
            <button onClick={() => setShowAll(true)} className="w-full text-center text-[11.5px] text-sky-700 font-bold py-2">
              Показать всех — ещё {noTrials.length - noTrialsShown.length} преподов
            </button>
          )}
          {waiting.length === 0 && emptyCol(ql ? 'Никого не нашлось' : 'Очередь пуста')}
        </div>

        {/* ── Нужен созвон: при отсмотре первого урока нашли правки ── */}
        <div className="flex-1 min-w-[230px] bg-white border border-slate-200 rounded-2xl p-2.5">
          <ColHead color="#B45309" title="Нужен созвон" n={board.counts.needcall || 0} />
          {(board.needcall || []).map(t => <TeacherCard key={t.mkId} t={t} onReview={setReview} actions={assignBtn(t)} />)}
          {(board.needcall || []).length === 0 && emptyCol('Пока никого — сюда преподы попадают, если при отсмотре первого урока нашли правки')}
        </div>

        {/* ── Созвон назначен: слот-группами ── */}
        <div className="flex-1 min-w-[230px] bg-white border border-slate-200 rounded-2xl p-2.5">
          <ColHead color="#38BDF8" title="Созвон назначен"
            n={board.counts.scheduled + (board.counts.slots ? ' · ' + board.counts.slots + ' сл.' : '')} />
          {board.slots.length === 0 && emptyCol('Слотов пока нет — создайте окошки кнопкой «Слоты созвонов» справа сверху')}
          {board.slots.map(s => (
            <div key={s.id} className="border-2 border-sky-300 bg-sky-50 rounded-xl p-2 mb-2.5">
              <SlotHead s={s} busy={busy} onDelete={() => delSlot(s)} />
              {s.members.length > 0 && s.video_room && (
                <a href={slotRoomHref(s)} target="_blank" rel="noreferrer"
                  className="block text-center rounded-lg bg-sky-600 text-white text-[11.5px] font-bold py-1.5 mb-2 hover:bg-sky-700">
                  Начать созвон
                </a>
              )}
              <button disabled={busy} onClick={() => completeSlot(s)}
                className="block w-full text-center rounded-lg bg-emerald-600 text-white text-[11.5px] font-bold py-1.5 mb-2 hover:bg-emerald-700 disabled:opacity-50">
                Завершить созвон
              </button>
              {s.members.map(m => (
                <TeacherCard key={m.memberId} t={m} onReview={setReview} actions={
                  <>
                    <button disabled={busy} onClick={() => setCallOutcome({ teacher: m, slotId: s.id })}
                      className="mt-1.5 w-full rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-[11.5px] font-bold py-1.5 hover:bg-emerald-100">
                      Итог созвона
                    </button>
                    <button disabled={busy} onClick={() => removeMember(s.id, m.memberId)}
                      className="mt-1.5 w-full rounded-lg border border-slate-200 text-slate-400 text-[11px] py-1 hover:text-red-700 hover:border-red-200">
                      Убрать из слота
                    </button>
                  </>
                } />
              ))}
              {s.members.length === 0 && (
                <div className="text-[11px] text-slate-400 px-0.5 pb-1">Пока никто не записан</div>
              )}
            </div>
          ))}
        </div>


        {/* ── Итоговые колонки ── */}
        <div className="flex-1 min-w-[230px] bg-white border border-slate-200 rounded-2xl p-2.5">
          <ColHead color="#047857" title="Работают с нами" n={board.counts.working || board.counts.ok || 0} />
          {(board.working || []).map(t => <TeacherCard key={t.mkId} t={t} onReview={setReview} />)}
          {(board.working || []).length === 0 && emptyCol('Пока никого — сюда преподы попадают после отсмотра первого урока с итогом «работает с нами»')}
        </div>
        <div className="flex-1 min-w-[230px] bg-white border border-slate-200 rounded-2xl p-2.5">
          <ColHead color="#B45309" title="Требуют внимания" n={board.counts.watch} />
          {(board.watch || []).map(t => <TeacherCard key={t.mkId} t={t} onReview={setReview} />)}
          {(board.watch || []).length === 0 && emptyCol('Пока никого — «даём шанс, но контролируем»')}
        </div>
        <div className="flex-1 min-w-[230px] bg-white border border-slate-200 rounded-2xl p-2.5">
          <ColHead color="#B91C1C" title="Не подошли" n={board.counts.removed} />
          {(board.removed || []).map(t => <TeacherCard key={t.mkId} t={t} />)}
          {(board.removed || []).length === 0 && emptyCol('Пока никого')}
        </div>
      </div>

      {/* ── Окно «Слоты созвонов» ── */}
      {slotsOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-12" style={{ background: 'rgba(15,23,42,.45)' }}
          onClick={e => { if (e.target === e.currentTarget) setSlotsOpen(false) }}>
          <div className="bg-white rounded-2xl w-[560px] max-w-full max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-start gap-2.5 p-4 border-b border-slate-100">
              <div>
                <b className="text-[15px]">Слоты созвонов-обучений</b>
                <div className="text-[12px] text-slate-400 mt-0.5 leading-snug">
                  Вы выставляете окошки, в одном слоте до 3 преподов. Преподы записываются сами
                  из своего ЛК, либо сажайте их кнопкой «Назначить созвон» с доски.
                </div>
              </div>
              <button className="ml-auto text-slate-400 text-lg px-1.5" onClick={() => setSlotsOpen(false)}>✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex items-center gap-2 border border-dashed border-sky-200 bg-sky-50 rounded-xl p-2.5 mb-2.5">
                <input type="date" value={newDate} min={isoToday()} onChange={e => setNewDate(e.target.value)}
                  className="border border-sky-200 rounded-lg px-2 py-1.5 text-[12.5px] bg-white outline-none" />
                <input type="time" value={newTime} onChange={e => setNewTime(e.target.value)}
                  className="border border-sky-200 rounded-lg px-2 py-1.5 text-[12.5px] bg-white outline-none" />
                <button disabled={busy} onClick={addSlot}
                  className="ml-auto rounded-lg bg-sky-600 text-white text-[12.5px] font-bold px-3 py-1.5 disabled:opacity-50">
                  Добавить слот
                </button>
              </div>
              {board.slots.length === 0 && <div className="text-[12px] text-slate-400 text-center py-4">Окошек пока нет</div>}
              {board.slots.map(s => (
                <div key={s.id} className="border-2 border-sky-300 rounded-xl p-3 mb-2">
                  <SlotHead s={s} busy={busy} onDelete={() => delSlot(s)} wide />
                  <div className="mt-2 flex flex-col gap-1">
                    {s.members.map(m => (
                      <div key={m.memberId} className="flex items-center gap-2 text-[12px]">
                        {m.name}
                        <span className="text-[10.5px] text-slate-400">{(m.subjects || []).join(', ')}</span>
                        <button disabled={busy} title="Убрать из слота" onClick={() => removeMember(s.id, m.memberId)}
                          className="ml-auto text-slate-300 hover:text-red-600">✕</button>
                      </div>
                    ))}
                    {s.members.length === 0 && (
                      <div className="text-[11.5px] text-slate-400">Пока никто не записан</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-100 p-3.5 text-[11px] text-slate-400 leading-relaxed">
              Препод из слота стоит в колонке «Созвон назначен». При удалении слота записанные
              преподы снимаются, каждому уходит уведомление об отмене.
            </div>
          </div>
        </div>
      )}

      {review && (
        <ReviewModal teacher={review} onClose={() => setReview(null)}
          onSaved={() => { setReview(null); load() }} />
      )}

      {statsOpen && <ManagerStatsModal onClose={() => setStatsOpen(false)} />}

      {callOutcome && (
        <CallOutcomeModal teacher={callOutcome.teacher} slotId={callOutcome.slotId}
          onClose={() => setCallOutcome(null)}
          onSaved={() => { setCallOutcome(null); load() }} />
      )}

      {/* ── Выбор слота для препода ── */}
      {pickFor && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-24" style={{ background: 'rgba(15,23,42,.45)' }}
          onClick={e => { if (e.target === e.currentTarget) setPickFor(null) }}>
          <div className="bg-white rounded-2xl w-[420px] max-w-full overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <b className="text-[14px]">Созвон для: {pickFor.name}</b>
              <div className="text-[12px] text-slate-400 mt-0.5">Выберите свободное окошко</div>
            </div>
            <div className="p-3 max-h-[50vh] overflow-y-auto">
              {board.slots.filter(s => s.members.length < s.capacity).map(s => (
                <button key={s.id} disabled={busy} onClick={() => assign(s, pickFor)}
                  className="w-full flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2.5 mb-1.5 text-[13px] hover:border-sky-300 hover:bg-sky-50">
                  <b>{fmtDay(s.slot_date)} · {s.slot_time}</b>
                  <span className="ml-auto text-[11px] text-slate-400">свободно {s.capacity - s.members.length}</span>
                </button>
              ))}
              {board.slots.filter(s => s.members.length < s.capacity).length === 0 && (
                <div className="text-[12.5px] text-slate-400 text-center py-4">
                  Свободных окошек нет — добавьте слот в «Слоты созвонов»
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Менеджер по преподавателям (22.10): «Действующие» (3+ проведённых урока, одна очередь с фильтрами) и «Новые»
// (меньше 3 уроков — прежняя доска Отбора). Остальные роли видят доску как раньше.
export default function OtborTab() {
  const isTutors = localStorage.getItem('role') === 'tutor_manager'
  const [tab, setTab] = useState('active')
  const [n, setN] = useState(null)
  const [nNew, setNNew] = useState(null)
  if (!isTutors) return <OtborBoard />
  const pill = (k, label, count) => (
    <button onClick={() => setTab(k)} className="rounded-full text-[13px] font-bold px-3.5 py-1.5 border"
      style={tab === k ? { background: '#0369A1', borderColor: '#0369A1', color: '#fff' } : { background: '#fff', borderColor: '#E2E8F0', color: '#475569' }}>
      {label}{count != null && <span className="font-semibold"> {count}</span>}
    </button>
  )
  return (
    <div>
      <div className="px-4 pt-4 flex items-center gap-2.5">
        <h1 className="text-[17px] font-bold mr-1">Созвоны</h1>
        {pill('active', 'Действующие', n)}
        {pill('new', 'Новые', nNew)}
      </div>
      {/* обе вкладки держим смонтированными: цифры на кнопках считаются сразу, без перезагрузки при переключении */}
      <div style={{ display: tab === 'active' ? '' : 'none' }} className="px-4 pt-3 pb-10"><OtborActive onCounts={setN} /></div>
      <div style={{ display: tab === 'new' ? '' : 'none' }}><OtborBoard scope="new" onCounts={setNNew} /></div>
    </div>
  )
}
